import { Fragment, ReactNode, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Switch, Typography } from '@mui/material';
import validator from '@rjsf/validator-ajv8';
import { RJSFSchema } from '@rjsf/utils';
import { colors } from '@src/styles';
import { IdReferenceKind, ReferenceLookupContext, useReferenceLookup } from './references/referenceLookupContext';
import { useOrganizationWorkflowNames } from './references/ReferenceLookupWidget';

const monoFontFamily = "'JetBrains Mono', monospace";

const COMMIT_DELAY_MS = 250;
const titleFontFamily = "'Inter Tight', 'Inter', sans-serif";

type TJson = Record<string, unknown>;
type TSchema = RJSFSchema & {
  properties?: Record<string, TSchema>;
  items?: TSchema;
  required?: string[];
  maxItems?: number;
};
type TRenderForm = (
  schema: object,
  formData: unknown,
  onChange: (value: unknown) => void,
  uiSchema?: object,
) => ReactNode;

const STAGE_LABELS: Record<string, string> = {
  ticketValidation: 'Validação do ticket',
  inspectorAnalysis: 'Análise via Inspector',
  rating: 'Rating',
  decision: 'Decisão',
  irpf: 'Análise de IRPF',
  consents: 'Anuências',
  validation: 'Validação do cliente',
  ticketFields: 'Campos do ticket',
  pipelineConnections: 'Conexões de pipeline',
  toPhaseIds: 'Mapeamento de fases',
  workflows: 'Workflows',
  drive: 'Drive',
  dre: 'DRE',
  balance: 'Balanço',
  'credit-review': 'Revisão de crédito',
  'collateral-assets': 'Garantias',
  'ticket-transition': 'Transição do ticket',
  'ticket-decision': 'Decisão do ticket',
  'ticket-expired/deleted': 'Ticket expirado ou excluído',
  'ticket-updated': 'Ticket atualizado',
};

const STAGE_LABELS_BY_PATH: Record<string, string> = {
  'ticket.irpf': 'IRPF',
  'customer.irpf': 'IRPF',
};

const GROUP_LABELS: Record<string, string> = {
  ticket: 'Documentos do ticket',
  customer: 'Documentos do cliente',
};

interface ItemNoun {
  singular: string;
  plural: string;
  isFeminine: boolean;
}

const ITEM_NOUNS: Record<string, ItemNoun> = {
  workflows: { singular: 'Workflow', plural: 'Workflows', isFeminine: false },
  sourceOrganizations: { singular: 'Organização', plural: 'Organizações', isFeminine: true },
  sources: { singular: 'Origem', plural: 'Origens', isFeminine: true },
  destinations: { singular: 'Destino', plural: 'Destinos', isFeminine: false },
  pipelineConnections: { singular: 'Conexão', plural: 'Conexões', isFeminine: true },
  toPhaseIds: { singular: 'Mapeamento', plural: 'Mapeamentos', isFeminine: false },
  ticketFields: { singular: 'Campo', plural: 'Campos', isFeminine: false },
};

const DEFAULT_ITEM_NOUN: ItemNoun = { singular: 'Item', plural: 'Itens', isFeminine: false };

const countItems = (collectionKey: string, count: number): string => {
  const noun = ITEM_NOUNS[collectionKey] ?? DEFAULT_ITEM_NOUN;
  return `${count} ${(count === 1 ? noun.singular : noun.plural).toLowerCase()}`;
};

const COLLECTION_ID_REFERENCES: Record<string, IdReferenceKind> = {
  workflows: 'workflow',
  sourceOrganizations: 'organization',
};

const STAGE_DISPLAY_POSITIONS: Record<string, number> = { ticketValidation: 0 };

const COUNT_LABELS: Record<string, [string, string]> = {
  expressions: ['regra', 'regras'],
  types: ['tipo', 'tipos'],
  customFields: ['campo', 'campos'],
  triggerFields: ['campo gatilho', 'campos gatilho'],
};

const monoLabelSX = {
  fontFamily: monoFontFamily,
  fontSize: '10px',
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: colors.brand.textMuted,
} as const;

interface StageEntry {
  id: string;
  path: string[];
  node: TSchema;
  isRequired: boolean;
  group?: string;
}

const isPlainObject = (value: unknown): value is TJson =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isCollectionSchema = (node?: TSchema): boolean =>
  node?.type === 'array' && isPlainObject(node.items) && node.items.type === 'object' && Boolean(node.items.properties);

const isStageSchema = (node?: TSchema): boolean => node?.type === 'object' || isCollectionSchema(node);

const isGroupSchema = (node?: TSchema): boolean => {
  if (node?.type !== 'object' || node.dependencies || !node.properties) return false;
  const children = Object.values(node.properties);
  return children.length >= 2 && children.every((child) => isStageSchema(child));
};

export const getCollectionKey = (schema: object): string | null => {
  const properties = (schema as TSchema)?.properties;
  if (!properties) return null;
  const keys = Object.keys(properties);
  if (keys.length !== 1) return null;
  return isCollectionSchema(properties[keys[0]]) ? keys[0] : null;
};

const orderStageEntries = (entries: StageEntry[]): StageEntry[] => {
  const ordered = entries.filter((entry) => !(entry.id in STAGE_DISPLAY_POSITIONS));
  entries
    .filter((entry) => entry.id in STAGE_DISPLAY_POSITIONS)
    .sort((a, b) => STAGE_DISPLAY_POSITIONS[a.id] - STAGE_DISPLAY_POSITIONS[b.id])
    .forEach((entry) => ordered.splice(Math.min(STAGE_DISPLAY_POSITIONS[entry.id], ordered.length), 0, entry));
  return ordered;
};

const getStageEntries = (schema?: TSchema): StageEntry[] => {
  const requiredKeys = schema?.required ?? [];
  const entries = Object.entries(schema?.properties ?? {}).flatMap(([key, node]): StageEntry[] => {
    if (isGroupSchema(node)) {
      const groupRequired = node.required ?? [];
      return Object.entries(node.properties ?? {}).map(([childKey, childNode]) => ({
        id: `${key}.${childKey}`,
        path: [key, childKey],
        node: childNode,
        isRequired: requiredKeys.includes(key) && groupRequired.includes(childKey),
        group: key,
      }));
    }
    if (!isStageSchema(node)) return [];
    return [{ id: key, path: [key], node, isRequired: requiredKeys.includes(key) }];
  });
  return orderStageEntries(entries);
};

const getDependentKeys = (schema: TSchema): string[] => {
  const keys = new Set<string>();
  Object.values(schema.dependencies ?? {}).forEach((dependency) => {
    const variants =
      isPlainObject(dependency) && Array.isArray(dependency.oneOf) ? (dependency.oneOf as TSchema[]) : [];
    variants.forEach((variant) => Object.keys(variant.properties ?? {}).forEach((key) => keys.add(key)));
  });
  return [...keys].filter((key) => !(schema.properties && key in schema.properties));
};

const mergeBasicValues = (source: TJson, formValue: unknown, keys: string[]): TJson => {
  const next = { ...source };
  keys.forEach((key) => delete next[key]);
  return { ...next, ...(isPlainObject(formValue) ? formValue : {}) };
};

const getBasicKeys = (schema: TSchema): string[] =>
  Object.entries(schema.properties ?? {})
    .filter(([, node]) => !isStageSchema(node))
    .map(([key]) => key);

export const hasConfigFields = (schema?: object): boolean =>
  Object.keys((schema as TSchema | undefined)?.properties ?? {}).length > 0;

const getStageLabel = (entry: StageEntry): string => {
  const key = entry.path[entry.path.length - 1];
  return (
    STAGE_LABELS_BY_PATH[entry.id] ??
    STAGE_LABELS[key] ??
    (typeof entry.node.title === 'string' ? entry.node.title : key)
  );
};

const getGroupLabel = (group: string, schema: TSchema): string => {
  const title = schema.properties?.[group]?.title;
  return GROUP_LABELS[group] ?? (typeof title === 'string' ? title : group);
};

const getIn = (source: unknown, path: string[]): unknown =>
  path.reduce<unknown>((current, key) => (isPlainObject(current) ? current[key] : undefined), source);

const setIn = (source: TJson, path: string[], value: unknown): TJson => {
  const [head, ...rest] = path;
  const next = { ...source };
  if (rest.length === 0) {
    if (value === undefined) delete next[head];
    else next[head] = value;
    return next;
  }
  const updated = setIn(isPlainObject(source[head]) ? (source[head] as TJson) : {}, rest, value);
  if (Object.keys(updated).length === 0) delete next[head];
  else next[head] = updated;
  return next;
};

const summarizeStage = (value: unknown): string => {
  if (Array.isArray(value)) return `${value.length} ${value.length === 1 ? 'item' : 'itens'}`;
  if (!isPlainObject(value)) return '';
  const parts: string[] = [];
  if (typeof value.enabled === 'boolean') parts.push(value.enabled ? 'ativado' : 'desativado');
  if (typeof value.strategy === 'string') parts.push(value.strategy);
  Object.entries(value).forEach(([key, child]) => {
    if (!Array.isArray(child)) return;
    const [singular, plural] = COUNT_LABELS[key] ?? ['item', 'itens'];
    parts.push(`${child.length} ${child.length === 1 ? singular : plural}`);
  });
  const filled = Object.entries(value).filter(
    ([key, child]) =>
      key !== 'strategy' && key !== 'enabled' && !Array.isArray(child) && child !== undefined && child !== '',
  ).length;
  if (filled > 0) parts.push(`${filled} ${filled === 1 ? 'campo' : 'campos'}`);
  return parts.join(' · ');
};

const getInitialFieldValue = (node: TSchema): unknown => {
  if (node.const !== undefined) return node.const;
  if (Array.isArray(node.enum) && node.enum.length > 0) return node.enum[0];
  if (node.type === 'boolean') return false;
  if (node.type === 'array') return [];
  if (node.type === 'object') {
    const value = getInitialStageValue(node);
    return isPlainObject(value) && Object.keys(value).length > 0 ? value : undefined;
  }
  return undefined;
};

const getFirstVariant = (node?: TSchema): TSchema | undefined => {
  const variants = (node?.anyOf ?? node?.oneOf) as TSchema[] | undefined;
  return Array.isArray(variants) ? variants.find((variant) => isPlainObject(variant) && variant.properties) : undefined;
};

export const getInitialStageValue = (node?: TSchema): unknown => {
  if (node?.type === 'array') return [];
  const firstVariant = getFirstVariant(node);
  if (firstVariant && !node?.properties) return getInitialStageValue(firstVariant);
  const value: TJson = {};
  const strategyEnum = (node?.properties?.strategy as TSchema | undefined)?.enum;
  if (Array.isArray(strategyEnum) && strategyEnum.length > 0) value.strategy = strategyEnum[0];
  (node?.required ?? []).forEach((key) => {
    const child = node?.properties?.[key];
    if (!child || value[key] !== undefined) return;
    const initial = getInitialFieldValue(child);
    if (initial !== undefined) value[key] = initial;
  });
  return value;
};

const TOGGLE_KEY = 'enabled';

const TOGGLE_UI_SCHEMA = { [TOGGLE_KEY]: { 'ui:widget': 'hidden' } };

const isBooleanNode = (node?: TSchema): boolean => node?.type === 'boolean' || typeof node?.const === 'boolean';

const getToggleExtraKeys = (node: TSchema): string[] | null => {
  if (node.type !== 'object') return null;
  const variants = ((node.anyOf ?? node.oneOf) as TSchema[] | undefined)?.filter(isPlainObject) ?? [];
  const shapes = variants.length > 0 ? variants : [node];
  if (!shapes.every((shape) => isBooleanNode(shape.properties?.[TOGGLE_KEY]))) return null;
  const keys = new Set<string>();
  [node, ...shapes].forEach((shape) =>
    Object.keys(shape.properties ?? {}).forEach((key) => {
      if (key !== TOGGLE_KEY) keys.add(key);
    }),
  );
  return [...keys];
};

const withoutTitle = (node: TSchema): object => {
  const copy: Record<string, unknown> = { ...(node as Record<string, unknown>) };
  delete copy.title;
  return copy;
};

const countErrors = (data: unknown, schema: TSchema): number => {
  try {
    return validator.validateFormData(data, schema).errors.length;
  } catch {
    return 0;
  }
};

const shortId = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value) return null;
  return value.length > 12 ? `${value.slice(0, 4)}…${value.slice(-3)}` : value;
};

const useBufferedCommit = (value: TJson, onChange: (value: TJson) => void) => {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const pendingRef = useRef<TJson | null>(null);
  const timerRef = useRef<number | undefined>(undefined);

  const flush = useCallback(() => {
    window.clearTimeout(timerRef.current);
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    onChangeRef.current(pending);
  }, []);

  useEffect(() => flush, [flush]);

  const getLatest = (): TJson => pendingRef.current ?? value;

  const commit = (next: TJson, isImmediate: boolean) => {
    pendingRef.current = next;
    window.clearTimeout(timerRef.current);
    if (isImmediate) flush();
    else timerRef.current = window.setTimeout(flush, COMMIT_DELAY_MS);
  };

  return { getLatest, commit, flush };
};

const switchSX = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: colors.primary.yellow },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: colors.primary.yellow,
    opacity: 0.45,
  },
  '& .MuiSwitch-track': { backgroundColor: colors.brand.textMuted },
} as const;

interface StageListProps {
  schema: TSchema;
  value: TJson;
  onChange: (value: TJson, isImmediate?: boolean) => void;
  getLatest: () => TJson;
  renderForm: TRenderForm;
  readOnly: boolean;
  basicLookup: object;
  stageLookup: object;
}

const StageList = ({
  schema,
  value,
  onChange,
  getLatest,
  renderForm,
  readOnly,
  basicLookup,
  stageLookup,
}: StageListProps) => {
  const [expandedStage, setExpandedStage] = useState<string | null | undefined>(undefined);
  const entries = useMemo(() => getStageEntries(schema), [schema]);
  const basicKeys = useMemo(() => getBasicKeys(schema), [schema]);
  const basicSchema = useMemo(
    () => ({
      type: 'object',
      required: (schema.required ?? []).filter((key) => basicKeys.includes(key)),
      properties: Object.fromEntries(basicKeys.map((key) => [key, schema.properties?.[key]])),
      ...(schema.dependencies ? { dependencies: schema.dependencies } : {}),
    }),
    [schema, basicKeys],
  );
  const dependentKeys = useMemo(() => getDependentKeys(schema), [schema]);
  const stageSchemas = useMemo(
    () => Object.fromEntries(entries.map((entry) => [entry.id, withoutTitle(entry.node)])),
    [entries],
  );
  const toggleStages = useMemo(
    () =>
      Object.fromEntries(
        entries
          .filter((entry) => entry.isRequired)
          .map((entry) => [entry.id, getToggleExtraKeys(entry.node)])
          .filter(([, extraKeys]) => extraKeys !== null),
      ) as Record<string, string[]>,
    [entries],
  );

  const isToggleOn = (entry: StageEntry): boolean => {
    const stageValue = getIn(value, entry.path);
    return isPlainObject(stageValue) && stageValue[TOGGLE_KEY] === true;
  };

  const canExpandStage = (entry: StageEntry): boolean => {
    const toggleExtras = toggleStages[entry.id];
    if (toggleExtras) return isToggleOn(entry) && toggleExtras.length > 0;
    return getIn(value, entry.path) !== undefined;
  };

  const firstActiveStage = entries.find((entry) => canExpandStage(entry))?.id ?? null;
  const openStage = expandedStage === undefined ? firstActiveStage : expandedStage;

  const updateStage = (path: string[], nextValue: unknown, isImmediate = false) =>
    onChange(setIn(getLatest(), path, nextValue), isImmediate);

  useEffect(() => {
    if (readOnly) return;
    const missing = entries.filter((entry) => toggleStages[entry.id] && getIn(value, entry.path) === undefined);
    if (missing.length === 0) return;
    onChange(
      missing.reduce<TJson>((current, entry) => setIn(current, entry.path, { [TOGGLE_KEY]: false }), getLatest()),
      true,
    );
  }, [entries, toggleStages, readOnly, value]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {basicKeys.length > 0 && (
        <ReferenceLookupContext.Provider value={basicLookup}>
          {renderForm(
            basicSchema,
            Object.fromEntries([...basicKeys, ...dependentKeys].map((key) => [key, value[key]])),
            (formValue) => onChange(mergeBasicValues(getLatest(), formValue, [...basicKeys, ...dependentKeys])),
          )}
        </ReferenceLookupContext.Provider>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {entries.map((entry, stageIndex) => {
          const stageValue = getIn(value, entry.path);
          const isActive = stageValue !== undefined;
          const toggleExtras = toggleStages[entry.id];
          const isToggle = Boolean(toggleExtras);
          const isHighlighted = isToggle ? isToggleOn(entry) : isActive;
          const canExpand = canExpandStage(entry);
          const isExpanded = openStage === entry.id && canExpand;
          const isLast = stageIndex === entries.length - 1;
          const stageErrors = isActive ? countErrors(stageValue, entry.node) : 0;
          const label = getStageLabel(entry);
          const startsGroup = Boolean(entry.group) && entries[stageIndex - 1]?.group !== entry.group;
          const collectionKey = isCollectionSchema(entry.node) ? entry.path[entry.path.length - 1] : null;

          return (
            <Fragment key={entry.id}>
              {startsGroup && entry.group && (
                <Typography
                  sx={{ ...monoLabelSX, marginLeft: '38px', marginTop: stageIndex > 0 ? 1 : 0, marginBottom: 1 }}
                >
                  {getGroupLabel(entry.group, schema)}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 26, flexShrink: 0 }}>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: '999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: monoFontFamily,
                      fontSize: '10.5px',
                      color: isHighlighted ? colors.primary.yellow : colors.brand.textMuted,
                      backgroundColor: isHighlighted ? 'rgba(255, 160, 21, 0.14)' : 'transparent',
                      border: `1px solid ${isHighlighted ? 'rgba(255, 160, 21, 0.5)' : colors.brand.line}`,
                    }}
                  >
                    {String(stageIndex + 1).padStart(2, '0')}
                  </Box>
                  {!isLast && <Box sx={{ flex: 1, width: '1px', backgroundColor: colors.brand.line }} />}
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    marginBottom: 1.25,
                    borderRadius: '12px',
                    border: `1px ${isActive ? 'solid' : 'dashed'} ${
                      stageErrors ? 'rgba(245, 158, 11, 0.45)' : colors.brand.line
                    }`,
                    backgroundColor: isActive ? colors.background.primary : 'transparent',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minHeight: 44, paddingX: 1.5 }}>
                    <Box
                      component="button"
                      type="button"
                      className="schema-custom-button"
                      disabled={!canExpand}
                      aria-expanded={isExpanded}
                      onClick={() => setExpandedStage(isExpanded ? null : entry.id)}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        height: 44,
                        padding: 0,
                        border: 'none',
                        backgroundColor: 'transparent',
                        textAlign: 'left',
                        cursor: canExpand ? 'pointer' : 'default',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '13.5px',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          color: isHighlighted ? colors.primary.white : colors.brand.textSecondary,
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography
                        noWrap
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: '12px',
                          color: stageErrors ? colors.status.pending : colors.brand.textMuted,
                        }}
                      >
                        {!isActive
                          ? readOnly
                            ? 'não configurada'
                            : 'desativada'
                          : stageErrors
                            ? `${stageErrors} pendência${stageErrors > 1 ? 's' : ''}`
                            : collectionKey && Array.isArray(stageValue)
                              ? countItems(collectionKey, stageValue.length)
                              : summarizeStage(stageValue)}
                      </Typography>
                      {canExpand && (
                        <Typography aria-hidden sx={{ fontSize: '12px', color: colors.brand.textMuted }}>
                          {isExpanded ? '▴' : '▾'}
                        </Typography>
                      )}
                    </Box>
                    {isToggle && (
                      <Switch
                        size="small"
                        checked={isHighlighted}
                        disabled={readOnly}
                        inputProps={{ 'aria-label': `Ativar ${label}` }}
                        onChange={(event) => {
                          const isChecked = event.target.checked;
                          const current = isPlainObject(stageValue) ? stageValue : {};
                          updateStage(
                            entry.path,
                            isChecked ? { ...current, [TOGGLE_KEY]: true } : { [TOGGLE_KEY]: false },
                            true,
                          );
                          setExpandedStage((previous) => {
                            if (isChecked && toggleExtras.length > 0) return entry.id;
                            return previous === entry.id ? null : previous;
                          });
                        }}
                        sx={switchSX}
                      />
                    )}
                    {!readOnly && !entry.isRequired && !isToggle && (
                      <Switch
                        size="small"
                        checked={isActive}
                        inputProps={{ 'aria-label': `Ativar ${label}` }}
                        onChange={(event) => {
                          updateStage(
                            entry.path,
                            event.target.checked ? getInitialStageValue(entry.node) : undefined,
                            true,
                          );
                          setExpandedStage(event.target.checked ? entry.id : null);
                        }}
                        sx={switchSX}
                      />
                    )}
                    {!readOnly && entry.isRequired && !isActive && !isToggle && (
                      <Box
                        component="button"
                        type="button"
                        className="schema-custom-button"
                        onClick={() => {
                          updateStage(entry.path, getInitialStageValue(entry.node), true);
                          setExpandedStage(entry.id);
                        }}
                        sx={{
                          height: 28,
                          paddingX: 1.25,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: colors.primary.yellow,
                          backgroundColor: 'rgba(255, 160, 21, 0.1)',
                          border: '1px solid rgba(255, 160, 21, 0.5)',
                        }}
                      >
                        Configurar
                      </Box>
                    )}
                  </Box>

                  {isExpanded && (
                    <Box sx={{ padding: '4px 14px 10px', borderTop: `1px solid ${colors.brand.line}` }}>
                      <ReferenceLookupContext.Provider value={stageLookup}>
                        {collectionKey ? (
                          <Box sx={{ paddingTop: 1.25 }}>
                            <CollectionBuilder
                              embedded
                              schema={{ type: 'object', properties: { [collectionKey]: entry.node } }}
                              collectionKey={collectionKey}
                              formData={{ [collectionKey]: stageValue }}
                              onChange={(nextRoot) =>
                                updateStage(
                                  entry.path,
                                  isPlainObject(nextRoot) ? nextRoot[collectionKey] : undefined,
                                  true,
                                )
                              }
                              renderForm={renderForm}
                              readOnly={readOnly}
                            />
                          </Box>
                        ) : (
                          renderForm(
                            stageSchemas[entry.id] ?? entry.node,
                            stageValue,
                            (nextValue) => updateStage(entry.path, nextValue),
                            isToggle ? TOGGLE_UI_SCHEMA : undefined,
                          )
                        )}
                      </ReferenceLookupContext.Provider>
                    </Box>
                  )}
                </Box>
              </Box>
            </Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

const JsonPreview = ({ title, value }: { title: string; value: unknown }) => (
  <Box
    component="aside"
    aria-label="JSON gerado"
    sx={{
      display: { xs: 'none', lg: 'flex' },
      flexDirection: 'column',
      minHeight: 0,
      borderRadius: '10px',
      border: `1px solid ${colors.brand.line}`,
      backgroundColor: colors.background.primary,
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: 36,
        paddingX: 1.5,
        borderBottom: `1px solid ${colors.brand.line}`,
      }}
    >
      <Typography sx={{ ...monoLabelSX, flexGrow: 1 }}>{title}</Typography>
    </Box>
    <Box
      component="pre"
      sx={{
        margin: 0,
        padding: '10px 12px',
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        fontFamily: monoFontFamily,
        fontSize: '11px',
        lineHeight: 1.65,
        color: colors.brand.textStrong,
        whiteSpace: 'pre',
      }}
    >
      {value ? JSON.stringify(value, null, 2) : '{}'}
    </Box>
  </Box>
);

export interface CollectionBuilderProps {
  schema: object;
  collectionKey: string;
  formData: unknown;
  onChange: (formData: unknown) => void;
  renderForm: TRenderForm;
  readOnly?: boolean;
  embedded?: boolean;
}

export const CollectionBuilder = ({
  schema,
  collectionKey,
  formData,
  onChange,
  renderForm,
  readOnly = false,
  embedded = false,
}: CollectionBuilderProps) => {
  const rootSchema = schema as TSchema;
  const collectionSchema = rootSchema.properties?.[collectionKey];
  const itemSchema = (collectionSchema?.items ?? {}) as TSchema;
  const root = isPlainObject(formData) ? formData : {};
  const items = (Array.isArray(root[collectionKey]) ? root[collectionKey] : []) as TJson[];
  const itemNoun = ITEM_NOUNS[collectionKey] ?? DEFAULT_ITEM_NOUN;
  const itemLabel = itemNoun.singular;
  const itemLower = itemLabel.toLowerCase();
  const newItemLabel = `+ ${itemNoun.isFeminine ? 'Nova' : 'Novo'} ${itemLower}`;
  const emptyLabel = `${itemNoun.isFeminine ? 'Nenhuma' : 'Nenhum'} ${itemLower} ${
    itemNoun.isFeminine ? 'configurada' : 'configurado'
  }`;
  const canAddItem =
    !readOnly && (collectionSchema?.maxItems === undefined || items.length < collectionSchema.maxItems);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const currentIndex = Math.min(selectedIndex, Math.max(items.length - 1, 0));
  const currentItem = items[currentIndex];

  const parentLookup = useReferenceLookup();
  const idReference = COLLECTION_ID_REFERENCES[collectionKey];
  const isWorkflowCollection = idReference === 'workflow';
  const workflowNames = useOrganizationWorkflowNames(isWorkflowCollection ? parentLookup.organizationId : undefined);
  const currentWorkflowId = isWorkflowCollection && typeof currentItem?.id === 'string' ? currentItem.id : undefined;
  const basicLookup = useMemo(
    () => ({ ...parentLookup, workflowId: currentWorkflowId, idReference, isWorkflowScope: isWorkflowCollection }),
    [parentLookup, currentWorkflowId, idReference, isWorkflowCollection],
  );
  const stageLookup = useMemo(
    () => ({
      ...parentLookup,
      workflowId: currentWorkflowId,
      idReference: undefined,
      isWorkflowScope: isWorkflowCollection,
    }),
    [parentLookup, currentWorkflowId, isWorkflowCollection],
  );
  const getItemName = (item?: TJson): string | undefined =>
    typeof item?.id === 'string' ? workflowNames[item.id] : undefined;

  const stageEntries = useMemo(() => getStageEntries(itemSchema), [itemSchema]);
  const deferredItems = useDeferredValue(items);
  const errorsByItem = useMemo(
    () => deferredItems.map((item) => countErrors(item, itemSchema)),
    [deferredItems, itemSchema],
  );
  const totalErrors = errorsByItem.reduce((sum, count) => sum + count, 0);

  const { getLatest, commit, flush } = useBufferedCommit(root, onChange);

  const getLatestItems = (): TJson[] => {
    const latest = getLatest()[collectionKey];
    return Array.isArray(latest) ? (latest as TJson[]) : [];
  };

  const emitItems = (nextItems: TJson[], isImmediate = true) =>
    commit({ ...getLatest(), [collectionKey]: nextItems }, isImmediate);

  const updateCurrent = (nextItem: TJson, isImmediate = false) =>
    emitItems(
      getLatestItems().map((item, index) => (index === currentIndex ? nextItem : item)),
      isImmediate,
    );

  const handleAddItem = () => {
    const nextItems = [...getLatestItems(), {}];
    emitItems(nextItems);
    setSelectedIndex(nextItems.length - 1);
  };

  const handleRemoveItem = () => {
    if (!window.confirm(`Remover ${itemLower} ${currentIndex + 1}? Essa ação não pode ser desfeita.`)) return;
    emitItems(getLatestItems().filter((_, index) => index !== currentIndex));
    setSelectedIndex(Math.max(currentIndex - 1, 0));
  };

  const handleSelectItem = (index: number) => {
    flush();
    setSelectedIndex(index);
  };

  return (
    <Box
      onBlurCapture={readOnly ? undefined : flush}
      sx={{
        display: 'grid',
        gridTemplateColumns: embedded
          ? { xs: '1fr', md: '200px minmax(0, 1fr)' }
          : { xs: '1fr', md: '220px minmax(0, 1fr)', lg: '220px minmax(0, 1fr) 280px' },
        gap: 2,
        height: embedded ? 'auto' : '100%',
        minHeight: 0,
        fontFamily: titleFontFamily,
      }}
    >
      <Box
        component="nav"
        aria-label={itemNoun.plural}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'row', md: 'column' },
          gap: 0.75,
          minHeight: 0,
          overflow: 'auto',
          paddingBottom: { xs: 1, md: 0 },
        }}
      >
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={monoLabelSX}>
            {itemNoun.plural} · {items.length}
          </Typography>
          <Typography sx={{ fontSize: '11.5px', color: totalErrors ? colors.status.pending : colors.status.concluded }}>
            {totalErrors ? `${totalErrors} pendência${totalErrors > 1 ? 's' : ''}` : 'tudo certo'}
          </Typography>
        </Box>

        {items.map((item, index) => {
          const isActive = index === currentIndex;
          const activeStages = stageEntries.filter((entry) => getIn(item, entry.path) !== undefined);
          return (
            <Box
              key={index}
              component="button"
              type="button"
              className="schema-custom-button"
              aria-current={isActive ? 'true' : undefined}
              onClick={() => handleSelectItem(index)}
              sx={{
                position: 'relative',
                flexShrink: 0,
                minWidth: { xs: 180, md: 0 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.5,
                padding: '9px 10px',
                textAlign: 'left',
                cursor: 'pointer',
                border: `1px solid ${isActive ? 'rgba(255, 160, 21, 0.4)' : colors.brand.line}`,
                borderRadius: '10px',
                backgroundColor: isActive ? 'rgba(255, 160, 21, 0.08)' : colors.brand.surface,
                '&:hover': { borderColor: colors.primary.yellow },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.primary.white, flexGrow: 1 }}>
                  {itemLabel} {index + 1}
                </Typography>
                {errorsByItem[index] > 0 && (
                  <Box
                    aria-label={`${errorsByItem[index]} pendências`}
                    sx={{ width: 7, height: 7, borderRadius: '999px', backgroundColor: colors.status.pending }}
                  />
                )}
              </Box>
              {getItemName(item) && (
                <Typography
                  noWrap
                  title={getItemName(item)}
                  sx={{ maxWidth: '100%', fontSize: '12px', color: colors.brand.textStrong }}
                >
                  {getItemName(item)}
                </Typography>
              )}
              {shortId(item.id) && (
                <Typography sx={{ fontFamily: monoFontFamily, fontSize: '10.5px', color: colors.brand.textMuted }}>
                  {shortId(item.id)}
                </Typography>
              )}
              {activeStages.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {activeStages.map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        fontFamily: monoFontFamily,
                        fontSize: '9px',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: colors.brand.textSecondary,
                        border: `1px solid ${colors.brand.line}`,
                        borderRadius: '999px',
                        padding: '1px 6px',
                      }}
                    >
                      {getStageLabel(entry).split(' ')[0]}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          );
        })}

        {canAddItem && (
          <Box
            component="button"
            type="button"
            className="schema-add-item"
            onClick={handleAddItem}
            sx={{
              flexShrink: 0,
              height: 34,
              paddingX: 1.25,
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '12.5px',
              fontWeight: 500,
              color: colors.brand.textSecondary,
              backgroundColor: 'transparent',
              border: '1px dashed rgba(255, 255, 255, 0.18)',
              '&:hover': { borderColor: colors.primary.yellow, color: colors.primary.yellow },
            }}
          >
            {newItemLabel}
          </Box>
        )}
      </Box>

      <Box sx={{ minHeight: 0, overflowY: embedded ? 'visible' : 'auto', paddingRight: 0.5 }}>
        {!currentItem ? (
          <Box sx={{ padding: '32px 8px', textAlign: 'center' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: colors.primary.white }}>
              {emptyLabel}
            </Typography>
            {canAddItem && (
              <Typography sx={{ marginTop: 0.5, fontSize: '12.5px', color: colors.brand.textSecondary }}>
                Use &quot;{newItemLabel}&quot; para começar.
              </Typography>
            )}
          </Box>
        ) : (
          <Box key={currentIndex} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                noWrap
                sx={{ fontSize: '16px', fontWeight: 600, color: colors.primary.white, flexGrow: 1, minWidth: 0 }}
              >
                {itemLabel} {currentIndex + 1}
                {getItemName(currentItem) && (
                  <Box component="span" sx={{ fontWeight: 400, color: colors.brand.textSecondary }}>
                    {' · '}
                    {getItemName(currentItem)}
                  </Box>
                )}
              </Typography>
              {!readOnly && (
                <Box
                  component="button"
                  type="button"
                  className="schema-custom-button"
                  onClick={handleRemoveItem}
                  sx={{
                    height: 30,
                    paddingX: 1.25,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: colors.status.rejected,
                    backgroundColor: 'transparent',
                    border: `1px solid ${colors.status.rejected}55`,
                    '&:hover': { backgroundColor: `${colors.status.rejected}14` },
                  }}
                >
                  Remover {itemLower}
                </Box>
              )}
            </Box>

            <StageList
              schema={itemSchema}
              value={currentItem}
              onChange={(nextItem, isImmediate) => updateCurrent(nextItem, isImmediate)}
              getLatest={() => getLatestItems()[currentIndex] ?? {}}
              renderForm={renderForm}
              readOnly={readOnly}
              basicLookup={basicLookup}
              stageLookup={stageLookup}
            />
          </Box>
        )}
      </Box>

      {!embedded && (
        <JsonPreview title={`JSON · ${itemLower} ${currentItem ? currentIndex + 1 : '—'}`} value={currentItem} />
      )}
    </Box>
  );
};

export interface ConfigStageBuilderProps {
  schema: object;
  formData: unknown;
  onChange: (formData: unknown) => void;
  renderForm: TRenderForm;
  readOnly?: boolean;
}

export const ConfigStageBuilder = ({
  schema,
  formData,
  onChange,
  renderForm,
  readOnly = false,
}: ConfigStageBuilderProps) => {
  const rootSchema = schema as TSchema;
  const root = isPlainObject(formData) ? formData : {};
  const parentLookup = useReferenceLookup();
  const lookup = useMemo(
    () => ({ ...parentLookup, workflowId: undefined, idReference: undefined, isWorkflowScope: false }),
    [parentLookup],
  );
  const deferredRoot = useDeferredValue(root);
  const totalErrors = useMemo(() => countErrors(deferredRoot, rootSchema), [deferredRoot, rootSchema]);
  const { getLatest, commit, flush } = useBufferedCommit(root, onChange);

  return (
    <Box
      onBlurCapture={readOnly ? undefined : flush}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 280px' },
        gap: 2,
        height: '100%',
        minHeight: 0,
        fontFamily: titleFontFamily,
      }}
    >
      <Box sx={{ minHeight: 0, overflowY: 'auto', paddingRight: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1.5 }}>
          <Typography sx={monoLabelSX}>Configuração</Typography>
          <Typography sx={{ fontSize: '11.5px', color: totalErrors ? colors.status.pending : colors.status.concluded }}>
            {totalErrors ? `${totalErrors} pendência${totalErrors > 1 ? 's' : ''}` : 'tudo certo'}
          </Typography>
        </Box>
        <StageList
          schema={rootSchema}
          value={root}
          onChange={(nextRoot, isImmediate) => commit(nextRoot, Boolean(isImmediate))}
          getLatest={getLatest}
          renderForm={renderForm}
          readOnly={readOnly}
          basicLookup={lookup}
          stageLookup={lookup}
        />
      </Box>
      <JsonPreview title="JSON · configuração" value={root} />
    </Box>
  );
};
